import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Target, Users, Lightbulb, CheckCircle, Mail, Linkedin, ArrowRight } from 'lucide-react';

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const mailtoLink = "mailto:hello@planniq.at?subject=Pilotgespräch%20planniq%20%E2%80%93%20Interesse%20an%20Austausch&body=Hallo%20planniq-Team%2C%0A%0Aich%20interessiere%20mich%20f%C3%BCr%20einen%20Austausch%20mit%20euch%20zum%20Thema%20Budgetsteuerung%20und%20w%C3%BCrde%20gerne%20mehr%20%C3%BCber%20eure%20Arbeit%20erfahren.%0A%0AMein%20Name%3A%20%0AMein%20Unternehmen%3A%20%0AMeine%20Rolle%3A%20%0A%0AKurze%20Beschreibung%20meines%20Interesses%3A%0A%0A%0AFreundliche%20Gr%C3%BC%C3%9Fe";

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-page)' }}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b" style={{ background: 'var(--bg-page)', borderColor: 'var(--border-light)' }}>
        <div className="container">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <span className="text-2xl font-bold" style={{ color: 'var(--brand-primary)' }}>planniq</span>
            </div>

            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('problem')} className="body-small hover:opacity-80 transition-opacity" style={{ color: 'var(--text-primary)' }}>Problem</button>
              <button onClick={() => scrollToSection('loesung')} className="body-small hover:opacity-80 transition-opacity" style={{ color: 'var(--text-primary)' }}>Ansatz</button>
              <button onClick={() => scrollToSection('zielgruppe')} className="body-small hover:opacity-80 transition-opacity" style={{ color: 'var(--text-primary)' }}>Für wen</button>
              <button onClick={() => scrollToSection('pilot')} className="body-small hover:opacity-80 transition-opacity" style={{ color: 'var(--text-primary)' }}>Pilotpartner</button>
              <a href={mailtoLink} className="btn-primary">Gespräch anfragen</a>
            </div>

            <button
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{ color: 'var(--brand-primary)' }}
            >
              <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                {mobileMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M3 12h18M3 6h18M3 18h18" />
                )}
              </svg>
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden pb-4 space-y-4">
              <button onClick={() => scrollToSection('problem')} className="block w-full text-left body-small py-2" style={{ color: 'var(--text-primary)' }}>Problem</button>
              <button onClick={() => scrollToSection('loesung')} className="block w-full text-left body-small py-2" style={{ color: 'var(--text-primary)' }}>Ansatz</button>
              <button onClick={() => scrollToSection('zielgruppe')} className="block w-full text-left body-small py-2" style={{ color: 'var(--text-primary)' }}>Für wen</button>
              <button onClick={() => scrollToSection('pilot')} className="block w-full text-left body-small py-2" style={{ color: 'var(--text-primary)' }}>Pilotpartner</button>
              <a href={mailtoLink} className="btn-primary w-full">Gespräch anfragen</a>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-40 pb-32 md:pt-56 md:pb-48">
        <div className="container">
          <div className="max-w-4xl">
            <h1 className="heading-1 mb-6" style={{ color: 'var(--text-primary)' }}>
              Budgetsteuerung für wachsende Teams – transparent und in Echtzeit
            </h1>
            <p className="body-large mb-10 max-w-2xl" style={{ color: 'var(--text-secondary)' }}>
              Operative Ausgaben im Blick behalten, bessere Entscheidungen treffen – ohne Excel und manuelle Workarounds.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={mailtoLink} className="btn-primary">
                <MessageSquare size={20} />
                Gespräch anfragen
              </a>
              <button onClick={() => scrollToSection('problem')} className="btn-secondary">
                Mehr erfahren
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section id="problem" className="py-20 md:py-32" style={{ background: 'var(--bg-light)' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="mb-12">
              <span className="body-small font-semibold" style={{ color: 'var(--brand-primary)' }}>PROBLEM</span>
              <h2 className="heading-2 mt-4" style={{ color: 'var(--text-primary)' }}>
                Budgets verteilen ist einfach – sie steuern nicht
              </h2>
            </div>
            <div className="space-y-6">
              <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                Teams bekommen Budgets, aber niemand weiß im Alltag genau, was noch verfügbar ist. Excel-Listen sind veraltet, Abstimmungen dauern zu lange, Entscheidungen werden ohne klare Datenbasis getroffen.
              </p>
              <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                Finance verliert den Überblick. Operative Teams treffen Ausgabenentscheidungen ohne klare Datenbasis. Das Ergebnis: Intransparenz und teure Überraschungen am Monatsende.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Lösung Section */}
      <section id="loesung" className="py-20 md:py-32">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="mb-12">
              <span className="body-small font-semibold" style={{ color: 'var(--brand-primary)' }}>ANSATZ</span>
              <h2 className="heading-2 mt-4" style={{ color: 'var(--text-primary)' }}>
                Operative Budgetsteuerung in Echtzeit
              </h2>
            </div>
            <p className="body-medium mb-10" style={{ color: 'var(--text-secondary)' }}>
              Unser Ansatz: Teams und Finance sollen einen gemeinsamen Überblick über laufende Budgets haben – was ausgegeben wurde, was noch verfügbar ist, wer was entscheiden kann. Ohne Excel, ohne Nachfragen.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="p-6 rounded-lg" style={{ background: 'var(--bg-card)' }}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: 'var(--brand-primary)' }}>
                  <Target size={24} style={{ color: 'var(--text-inverse)' }} />
                </div>
                <h3 className="heading-4 mb-3" style={{ color: 'var(--text-primary)' }}>Echtzeit-Überblick</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Aktuelle Budgetstände für alle Teams – ohne Excel-Updates.
                </p>
              </div>
              <div className="p-6 rounded-lg" style={{ background: 'var(--bg-card)' }}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: 'var(--brand-primary)' }}>
                  <CheckCircle size={24} style={{ color: 'var(--text-inverse)' }} />
                </div>
                <h3 className="heading-4 mb-3" style={{ color: 'var(--text-primary)' }}>Klare Entscheidungen</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Weniger Bauchgefühl, mehr fundierte Ausgabenentscheidungen im Alltag.
                </p>
              </div>
              <div className="p-6 rounded-lg" style={{ background: 'var(--bg-card)' }}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: 'var(--brand-primary)' }}>
                  <Lightbulb size={24} style={{ color: 'var(--text-inverse)' }} />
                </div>
                <h3 className="heading-4 mb-3" style={{ color: 'var(--text-primary)' }}>Volle Transparenz</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Ausgaben über Teams hinweg nachvollziehen – Finance behält die Kontrolle.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Zielgruppe Section */}
      <section id="zielgruppe" className="py-20 md:py-32" style={{ background: 'var(--bg-card)' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="mb-12">
              <span className="body-small font-semibold" style={{ color: 'var(--brand-primary)' }}>FÜR WEN</span>
              <h2 className="heading-2 mt-4" style={{ color: 'var(--text-primary)' }}>
                Wer profitiert von besserer Budgetsteuerung?
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="p-6 rounded-lg border" style={{ background: 'var(--bg-page)', borderColor: 'var(--border-light)' }}>
                <Users size={28} style={{ color: 'var(--brand-primary)' }} className="mb-3" />
                <h3 className="heading-4 mb-2" style={{ color: 'var(--text-primary)' }}>Finance & Controlling</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Teams, die operative Ausgaben über mehrere Bereiche hinweg im Blick behalten müssen – und heute mit veralteten Zahlen arbeiten.
                </p>
              </div>
              <div className="p-6 rounded-lg border" style={{ background: 'var(--bg-page)', borderColor: 'var(--border-light)' }}>
                <Target size={28} style={{ color: 'var(--brand-primary)' }} className="mb-3" />
                <h3 className="heading-4 mb-2" style={{ color: 'var(--text-primary)' }}>CFOs & Gründer:innen</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Entscheider, die Budgets steuern wollen, ohne täglich in Excel zu leben oder auf Berichte zu warten.
                </p>
              </div>
              <div className="p-6 rounded-lg border" style={{ background: 'var(--bg-page)', borderColor: 'var(--border-light)' }}>
                <Lightbulb size={28} style={{ color: 'var(--brand-primary)' }} className="mb-3" />
                <h3 className="heading-4 mb-2" style={{ color: 'var(--text-primary)' }}>Operative Leads</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Teamverantwortliche, die fundierte Ausgabenentscheidungen treffen wollen – ohne erst auf Finance-Updates warten zu müssen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pilot Partner Section */}
      <section id="pilot" className="py-20 md:py-32" style={{ background: 'var(--bg-light)' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-8">
              <span className="body-small font-semibold" style={{ color: 'var(--brand-primary)' }}>FRÜHE PHASE</span>
              <h2 className="heading-2 mt-4" style={{ color: 'var(--text-primary)' }}>
                Wir suchen Pilotpartner
              </h2>
            </div>
            <p className="body-large mb-10" style={{ color: 'var(--text-secondary)' }}>
              Wir sind früh. Das ist Absicht. Wir wollen operative Budgetsteuerung grundlegend neu denken – und suchen Unternehmen, die diesen Weg gemeinsam mit uns gehen und das Ergebnis mitgestalten.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-12 text-left">
              <div className="p-5 rounded-lg border" style={{ background: 'var(--bg-page)', borderColor: 'var(--border-light)' }}>
                <h4 className="heading-4 mb-2" style={{ color: 'var(--text-primary)' }}>Direkter Einfluss</h4>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Eure konkreten Anforderungen fließen direkt in die Produktentwicklung ein.
                </p>
              </div>
              <div className="p-5 rounded-lg border" style={{ background: 'var(--bg-page)', borderColor: 'var(--border-light)' }}>
                <h4 className="heading-4 mb-2" style={{ color: 'var(--text-primary)' }}>Nah am Team</h4>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Ihr arbeitet direkt mit uns zusammen – persönlich, schnell und auf Augenhöhe.
                </p>
              </div>
              <div className="p-5 rounded-lg border" style={{ background: 'var(--bg-page)', borderColor: 'var(--border-light)' }}>
                <h4 className="heading-4 mb-2" style={{ color: 'var(--text-primary)' }}>Frühzeitiger Zugang</h4>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Bevorzugter Einstieg und Sonderkonditionen für Pilotpartner.
                </p>
              </div>
            </div>
            <a href={mailtoLink} className="btn-primary inline-flex text-lg px-8 py-4">
              <Mail size={24} />
              Gespräch anfragen
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t" style={{ borderColor: 'var(--border-light)', background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <span className="text-2xl font-bold" style={{ color: 'var(--brand-primary)' }}>planniq</span>
              <p className="body-small mt-4" style={{ color: 'var(--text-secondary)' }}>
                Moderne Budgetsteuerung für wachsende Unternehmen.
              </p>
            </div>
            <div>
              <h4 className="heading-4 mb-4" style={{ color: 'var(--text-primary)' }}>Kontakt</h4>
              <div className="space-y-2">
                <a href="mailto:hello@planniq.at" className="body-small flex items-center gap-2 hover:opacity-80 transition-opacity" style={{ color: 'var(--text-secondary)' }}>
                  <Mail size={16} />
                  hello@planniq.at
                </a>
                <a href="#" className="body-small flex items-center gap-2 hover:opacity-80 transition-opacity" style={{ color: 'var(--text-secondary)' }}>
                  <Linkedin size={16} />
                  LinkedIn
                </a>
              </div>
            </div>
            <div>
              <h4 className="heading-4 mb-4" style={{ color: 'var(--text-primary)' }}>Rechtliches</h4>
              <div className="space-y-2">
                <button onClick={() => navigate('/impressum')} className="body-small block hover:opacity-80 transition-opacity text-left" style={{ color: 'var(--text-secondary)' }}>Impressum</button>
                <button onClick={() => navigate('/datenschutz')} className="body-small block hover:opacity-80 transition-opacity text-left" style={{ color: 'var(--text-secondary)' }}>Datenschutz</button>
              </div>
            </div>
          </div>
          <div className="pt-8 border-t" style={{ borderColor: 'var(--border-light)' }}>
            <p className="body-small text-center" style={{ color: 'var(--text-secondary)' }}>
              © 2026 planniq. Alle Rechte vorbehalten.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
